import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { 
  getCurrentSession, 
  getCurrentUser, 
  signInWithGoogle, 
  signInWithPassword,
  signOut, 
  refreshSession,
  onAuthStateChange,
  getUserProfile 
} from '../utils/authUtils';

// Create the context
const AuthContext = createContext(null);

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth Provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userProfile, setUserProfile] = useState(null);

  // Track visibility state to prevent extra auth checks
  // Using a ref instead of state to avoid triggering re-renders
  const lastVisibilityCheckRef = useRef(Date.now());

  // Handle auth state changes - but only for actual auth events, not visibility changes
  const handleAuthChange = useCallback(async (event, session) => {
    // Avoid unnecessary checks - don't reload on just visibility changes
    if (event === 'VISIBILITY_CHANGE') {
      // Just update the ref without triggering re-renders
      lastVisibilityCheckRef.current = Date.now();
      return;
    }
    
    // Don't set loading for frequent operations like visibility changes
    // Only set loading for real auth operations
    setLoading(true);
    
    if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
      try {
        // Get current user data
        const { user: currentUser, error: userError } = await getCurrentUser();
        
        if (userError) {
          console.error('Error fetching user after auth change:', userError);
          setError(userError);
          setLoading(false);
          return;
        }
        
        setUser(currentUser);
        setSession(session);
        
        // Fetch user profile data if available
        if (currentUser?.id) {
          const { profile, error: profileError } = await getUserProfile(currentUser.id);
          if (!profileError) {
            setUserProfile(profile);
          }
        }
      } catch (err) {
        console.error('Error handling auth state change:', err);
        setError(err);
      }
    } else if (event === 'SIGNED_OUT') {
      setUser(null);
      setSession(null);
      setUserProfile(null);
    }
    
    setLoading(false);
  }, []);
  
  // Check for existing session on mount and set up auth change listener
  // But avoid frequent rechecks that cause UI elements to collapse
  useEffect(() => {
    // Add a visibility change listener that DOESN'T trigger auth checks
    const handleVisibilityChange = () => {
      // Just mark that we had a visibility change without triggering re-renders
      // This prevents the full auth check when just switching tabs
      lastVisibilityCheckRef.current = Date.now();
    };
    
    // Add visibility listener without triggering auth checks
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    const checkAuth = async () => {
      try {
        setLoading(true);
        const { session: currentSession, error: sessionError } = await getCurrentSession();
        
        if (sessionError) {
          console.error('Error checking session:', sessionError);
          setError(sessionError);
          setLoading(false);
          return;
        }
        
        if (currentSession) {
          setSession(currentSession);
          
          // If we have a session, get the user details
          const { user: currentUser, error: userError } = await getCurrentUser();
          
          if (userError) {
            console.error('Error fetching user:', userError);
            setError(userError);
            setLoading(false);
            return;
          }
          
          setUser(currentUser);
          
          // Fetch user profile data if available
          if (currentUser?.id) {
            const { profile, error: profileError } = await getUserProfile(currentUser.id);
            if (!profileError) {
              setUserProfile(profile);
            }
          }
        }
      } catch (err) {
        console.error('Unexpected error during auth check:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    
    // Initial auth check
    checkAuth();
    
    // Set up auth state change listener
    const { data: authListener } = onAuthStateChange(handleAuthChange);
    
    // Clean up function
    return () => {
      if (authListener) {
        authListener.subscription.unsubscribe();
      }
      
      // Remove visibility listener
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [handleAuthChange]);

  // Function to manually refresh the token
  const handleRefreshSession = async () => {
    try {
      setLoading(true);
      const { session: refreshedSession, user: refreshedUser, error: refreshError } = await refreshSession();
      
      if (refreshError) {
        console.error('Error refreshing session:', refreshError);
        setError(refreshError);
        return { success: false, error: refreshError };
      }
      
      if (refreshedSession) {
        setSession(refreshedSession);
        setUser(refreshedUser);
        return { success: true };
      }
      
      return { success: false, error: new Error('Session refresh did not return session') };
    } catch (err) {
      console.error('Unexpected error during session refresh:', err);
      setError(err);
      return { success: false, error: err };
    } finally {
      setLoading(false);
    }
  };
  
  // The value to be provided to consumers of this context
  const value = {
    user,
    userProfile,
    session,
    loading,
    error,
    signInWithGoogle,
    signInWithPassword,
    signOut,
    refreshSession: handleRefreshSession,
    isAuthenticated: !!session,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
